import React, { useEffect, useState, useRef } from "react";
import { io } from "socket.io-client";
import "./SpaceBioMissionControl.css";

import PlantExperiment3D from "./PlantExperiment3D";

const SOCKET_URL = "/api/space-bio"

function SpaceBioMissionControl() {
    const [connected, setConnected] = useState(false);

    const [mission, setMission] = useState({
        mission_id: "BIO-001",
        mission_name: "Microgravity Plant Growth Experiment",
        status: "READY",
        mission_time: 0,
        score: 100,
    });

    const [telemetry, setTelemetry] = useState({
        temperature: 23.0,
        humidity: 60.0,
        co2: 850,
        oxygen: 20.9,
        ph: 7.0,
        light_intensity: 70,
        water_level: 100,
        nutrient_level: 100,
        biomass: 10.0,
        growth_rate: 0,
        stress: 0,
    });

    const [events, setEvents] = useState([]);

    const [commandStatus, setCommandStatus] = useState(
        "Waiting for mission..."
    );


    const eventContainerRef = useRef(null);

    useEffect(() => {
        const socket = io(
            window.location.origin,
            {
                transports: ["polling"]
            }
        );
        socket.on("connect", () => {
            setConnected(true);

            socket.emit("mission.subscribe", {
                mission_id: "BIO-001",
            });
        });

        socket.on("disconnect", () => {
            setConnected(false);
        });

        socket.on("mission.state", (data) => {
            if (data.mission) {
                setMission(data.mission);
            }

            if (data.telemetry) {
                setTelemetry(data.telemetry);
            }

            if (data.events) {
                setEvents(data.events);
            }
        });

        socket.on("telemetry.update", (data) => {
            setTelemetry(data);
        });

        socket.on("mission.update", (data) => {
            setMission((previous) => ({
                ...previous,
                ...data,
            }));
        });

        socket.on("mission.event", (event) => {
            setEvents((previous) => [
                ...previous.slice(-99),
                event,
            ]);
        });

        socket.on("command.result", (result) => {
            setCommandStatus(result.message);

            if (result.telemetry) {
                setTelemetry(result.telemetry);
            }

            if (result.mission) {
                setMission((previous) => ({
                    ...previous,
                    ...result.mission,
                }));
            }
        });

        return () => {
            socket.off("connect");
            socket.off("disconnect");
            socket.off("mission.state");
            socket.off("telemetry.update");
            socket.off("mission.update");
            socket.off("mission.event");
            socket.off("command.result");
        };
    }, []);

    useEffect(() => {
        if (eventContainerRef.current) {
            eventContainerRef.current.scrollTop =
                eventContainerRef.current.scrollHeight;
        }
    }, [events]);

    const sendCommand = (command, value = null) => {
        if (!connected) {
            setCommandStatus("Mission Control disconnected.");
            return;
        }

        socket.emit("mission.command", {
            mission_id: mission.mission_id,
            command,
            value,
        });
    };

    const startMission = () => {
        sendCommand("START_MISSION");
    };

    const pauseMission = () => {
        sendCommand("PAUSE_MISSION");
    };

    const resetMission = () => {
        sendCommand("RESET_MISSION");
    };

    const emergencyStop = () => {
        sendCommand("EMERGENCY_STOP");
    };

    const setLight = (value) => {
        sendCommand("SET_LIGHT", Number(value));
    };

    const setTemperature = (value) => {
        sendCommand("SET_TEMPERATURE", Number(value));
    };

    const setCO2 = (value) => {
        sendCommand("SET_CO2", Number(value));
    };

    const addWater = () => {
        sendCommand("ADD_WATER", 20);
    };

    const addNutrients = () => {
        sendCommand("ADD_NUTRIENTS", 20);
    };

    const formatMissionTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(
            2,
            "0"
        )}:${String(secs).padStart(2, "0")}`;
    };

    const getStatusClass = (status) => {
        return status
            .toLowerCase()
            .replaceAll(" ", "-");
    };

    const getTelemetryStatus = (value, min, max) => {
        if (value < min || value > max) {
            return "warning";
        }

        return "normal";
    };

    return (
        <div className="space-bio-mission-control">

            {/* HEADER */}

            <header className="sbmc-header">

                <div className="sbmc-brand">
                    <div className="sbmc-logo">
                        SB
                    </div>

                    <div>
                        <h1>
                            SPACE BIO MISSION CONTROL
                        </h1>

                        <div className="sbmc-subtitle">
                            STEMX365 Biological Experiment Operations
                        </div>
                    </div>
                </div>

                <div className="sbmc-header-status">

                    <div className="connection-status">
                        <span
                            className={
                                connected
                                    ? "connection-dot connected"
                                    : "connection-dot disconnected"
                            }
                        />

                        {connected
                            ? "CONNECTED"
                            : "DISCONNECTED"}
                    </div>

                    <div className="mission-clock">
                        T+ {formatMissionTime(mission.mission_time)}
                    </div>

                </div>

            </header>


            {/* MISSION BAR */}

            <section className="mission-bar">

                <div>
                    <span className="label">
                        MISSION
                    </span>

                    <strong>
                        {mission.mission_id}
                    </strong>
                </div>

                <div>
                    <span className="label">
                        EXPERIMENT
                    </span>

                    <strong>
                        {mission.mission_name}
                    </strong>
                </div>

                <div>
                    <span className="label">
                        STATUS
                    </span>

                    <span
                        className={`mission-status ${getStatusClass(
                            mission.status
                        )}`}
                    >
                        {mission.status}
                    </span>
                </div>

                <div>
                    <span className="label">
                        MISSION SCORE
                    </span>

                    <strong className="mission-score">
                        {mission.score}
                    </strong>
                </div>

            </section>


            {/* MAIN GRID */}

            <main className="sbmc-main">

                {/* EXPERIMENT */}

                <section className="panel experiment-panel">

                    <div className="panel-header">
                        <span>
                            BIOLOGICAL EXPERIMENT
                        </span>

                        <span className="panel-indicator">
                            LIVE
                        </span>
                    </div>

                    <div className="experiment-view">

                        <PlantExperiment3D />

                        <div className="experiment-overlay">

                            <div className="overlay-title">
                                PLANT GROWTH MODULE
                            </div>

                            <div className="overlay-data">
                                MICROGRAVITY
                            </div>

                        </div>

                    </div>

                    <div className="experiment-info">

                        <div>
                            <span>SPECIES</span>
                            <strong>Arabidopsis</strong>
                        </div>

                        <div>
                            <span>EXPERIMENT DAY</span>
                            <strong>
                                {Math.floor(
                                    mission.mission_time / 86400
                                ) + 1}
                            </strong>
                        </div>

                        <div>
                            <span>BIOMASS</span>
                            <strong>
                                {telemetry.biomass.toFixed(2)} g
                            </strong>
                        </div>

                    </div>

                </section>


                {/* TELEMETRY */}

                <section className="panel telemetry-panel">

                    <div className="panel-header">
                        <span>
                            BIOLOGICAL TELEMETRY
                        </span>

                        <span className="panel-indicator">
                            REAL TIME
                        </span>
                    </div>

                    <div className="telemetry-grid">

                        <TelemetryCard
                            name="TEMPERATURE"
                            value={`${telemetry.temperature.toFixed(1)} °C`}
                            status={getTelemetryStatus(
                                telemetry.temperature,
                                20,
                                26
                            )}
                        />

                        <TelemetryCard
                            name="HUMIDITY"
                            value={`${telemetry.humidity.toFixed(1)} %`}
                            status={getTelemetryStatus(
                                telemetry.humidity,
                                50,
                                75
                            )}
                        />

                        <TelemetryCard
                            name="CO₂"
                            value={`${telemetry.co2.toFixed(0)} ppm`}
                            status={getTelemetryStatus(
                                telemetry.co2,
                                700,
                                1200
                            )}
                        />

                        <TelemetryCard
                            name="O₂"
                            value={`${telemetry.oxygen.toFixed(1)} %`}
                            status={getTelemetryStatus(
                                telemetry.oxygen,
                                19,
                                22
                            )}
                        />

                        <TelemetryCard
                            name="PH"
                            value={telemetry.ph.toFixed(2)}
                            status={getTelemetryStatus(
                                telemetry.ph,
                                6.5,
                                7.5
                            )}
                        />

                        <TelemetryCard
                            name="LIGHT"
                            value={`${telemetry.light_intensity.toFixed(
                                0
                            )} %`}
                            status={getTelemetryStatus(
                                telemetry.light_intensity,
                                50,
                                90
                            )}
                        />

                        <TelemetryCard
                            name="WATER"
                            value={`${telemetry.water_level.toFixed(
                                0
                            )} %`}
                            status={getTelemetryStatus(
                                telemetry.water_level,
                                30,
                                100
                            )}
                        />

                        <TelemetryCard
                            name="NUTRIENTS"
                            value={`${telemetry.nutrient_level.toFixed(
                                0
                            )} %`}
                            status={getTelemetryStatus(
                                telemetry.nutrient_level,
                                30,
                                100
                            )}
                        />

                    </div>

                    <div className="growth-section">

                        <div className="growth-header">
                            <span>GROWTH RATE</span>

                            <strong>
                                {telemetry.growth_rate.toFixed(3)}
                            </strong>
                        </div>

                        <div className="growth-bar">

                            <div
                                className="growth-fill"
                                style={{
                                    width: `${Math.min(
                                        Math.max(
                                            telemetry.growth_rate * 100,
                                            0
                                        ),
                                        100
                                    )}%`,
                                }}
                            />

                        </div>

                    </div>

                    <div className="stress-section">

                        <div className="growth-header">
                            <span>BIOLOGICAL STRESS</span>

                            <strong>
                                {telemetry.stress.toFixed(1)} %
                            </strong>
                        </div>

                        <div className="stress-bar">

                            <div
                                className="stress-fill"
                                style={{
                                    width: `${Math.min(
                                        telemetry.stress,
                                        100
                                    )}%`,
                                }}
                            />

                        </div>

                    </div>

                </section>


                {/* COMMANDS */}

                <section className="panel commands-panel">

                    <div className="panel-header">
                        <span>
                            EXPERIMENT COMMANDS
                        </span>
                    </div>

                    <div className="command-group">

                        <label>
                            LIGHT INTENSITY
                        </label>

                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={telemetry.light_intensity}
                            onChange={(e) =>
                                setLight(e.target.value)
                            }
                        />

                        <div className="command-value">
                            {telemetry.light_intensity.toFixed(0)} %
                        </div>

                    </div>


                    <div className="command-group">

                        <label>
                            TEMPERATURE
                        </label>

                        <input
                            type="range"
                            min="15"
                            max="35"
                            step="0.5"
                            value={telemetry.temperature}
                            onChange={(e) =>
                                setTemperature(e.target.value)
                            }
                        />

                        <div className="command-value">
                            {telemetry.temperature.toFixed(1)} °C
                        </div>

                    </div>


                    <div className="command-group">

                        <label>
                            CO₂
                        </label>

                        <input
                            type="range"
                            min="300"
                            max="2000"
                            step="50"
                            value={telemetry.co2}
                            onChange={(e) =>
                                setCO2(e.target.value)
                            }
                        />

                        <div className="command-value">
                            {telemetry.co2.toFixed(0)} ppm
                        </div>

                    </div>


                    <div className="command-buttons">

                        <button
                            onClick={addWater}
                            className="command-button"
                        >
                            ADD WATER
                        </button>

                        <button
                            onClick={addNutrients}
                            className="command-button"
                        >
                            ADD NUTRIENTS
                        </button>

                    </div>

                </section>


                {/* MISSION CONTROL */}

                <section className="panel mission-control-panel">

                    <div className="panel-header">
                        <span>
                            MISSION CONTROL
                        </span>
                    </div>

                    <div className="mission-buttons">

                        <button
                            className="start-button"
                            onClick={startMission}
                        >
                            START MISSION
                        </button>

                        <button
                            className="pause-button"
                            onClick={pauseMission}
                        >
                            PAUSE
                        </button>

                        <button
                            className="reset-button"
                            onClick={resetMission}
                        >
                            RESET
                        </button>

                        <button
                            className="emergency-button"
                            onClick={emergencyStop}
                        >
                            EMERGENCY STOP
                        </button>

                    </div>

                    <div className="command-status">
                        {commandStatus}
                    </div>

                </section>


                {/* EVENT LOG */}

                <section className="panel event-panel">

                    <div className="panel-header">

                        <span>
                            MISSION EVENT LOG
                        </span>

                        <span>
                            {events.length} EVENTS
                        </span>

                    </div>

                    <div
                        className="event-log"
                        ref={eventContainerRef}
                    >

                        {events.length === 0 && (
                            <div className="empty-events">
                                No mission events.
                            </div>
                        )}

                        {events.map((event, index) => (

                            <div
                                className={`event-row ${event.level || "info"}`}
                                key={`${event.timestamp}-${index}`}
                            >

                                <span className="event-time">
                                    {event.timestamp}
                                </span>

                                <span className="event-level">
                                    {(event.level || "INFO").toUpperCase()}
                                </span>

                                <span className="event-message">
                                    {event.message}
                                </span>

                            </div>

                        ))}

                    </div>

                </section>

            </main>


            <footer className="sbmc-footer">
                STEMX365 SPACE BIOLOGY MISSION CONTROL
                <span>•</span>
                BIO-001
                <span>•</span>
                SIMULATION
            </footer>

        </div>
    );
}


function TelemetryCard({
    name,
    value,
    status,
}) {
    return (
        <div className={`telemetry-card ${status}`}>

            <div className="telemetry-name">
                {name}
            </div>

            <div className="telemetry-value">
                {value}
            </div>

            <div className="telemetry-status">
                {status === "normal"
                    ? "NOMINAL"
                    : "CHECK"}
            </div>

        </div>
    );
}


export default SpaceBioMissionControl;